import React, { useState, useEffect, useMemo } from 'react';
import { billingItemsAPI } from '../lib/api';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Search, ReceiptText, Pencil, Trash2, Lock } from 'lucide-react';

export default function BillingItems() {
  const enginePerms = useEffectivePermissions();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', category: '' });

  useEffect(() => { loadItems(); }, []);

  const loadItems = async () => {
    try {
      const { data } = await billingItemsAPI.list();
      setItems(data);
    } catch (e) {
      console.error('Failed to load billing items:', e);
      toast.error(e.response?.data?.detail || 'Failed to load billing items');
    } finally {
      setLoading(false);
    }
  };

  const categories = useMemo(
    () => [...new Set(items.map(i => i.category || 'Uncategorized'))].sort((a, b) => a.localeCompare(b)),
    [items]
  );

  const grouped = useMemo(() => {
    const q = search.toLowerCase().trim();
    const visible = items.filter(i => {
      if (!showInactive && !i.is_active) return false;
      if (!q) return true;
      return (i.name || '').toLowerCase().includes(q) || (i.category || '').toLowerCase().includes(q);
    });
    const map = new Map();
    visible.forEach(i => {
      const cat = i.category || 'Uncategorized';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(i);
    });
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [items, search, showInactive]);

  const activeCount = items.filter(i => i.is_active).length;

  const openCreate = () => {
    setEditingId(null);
    setForm({ name: '', category: '' });
    setShowDialog(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({ name: item.name, category: item.category });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.category.trim()) {
      toast.error('Name and category are required');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await billingItemsAPI.update(editingId, form);
        toast.success('Billing item updated');
      } else {
        await billingItemsAPI.create(form);
        toast.success('Billing item created');
      }
      setShowDialog(false);
      loadItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save billing item');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item) => {
    try {
      await billingItemsAPI.setActive(item.id, !item.is_active);
      toast.success(item.is_active ? `"${item.name}" deactivated` : `"${item.name}" reactivated`);
      loadItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete billing item "${item.name}"? If it's in use by job rate lines this will be blocked — deactivating is usually the right call.`)) return;
    try {
      await billingItemsAPI.delete(item.id);
      toast.success('Billing item deleted');
      loadItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete billing item');
    }
  };

  // Route-level guard: consistent with the Clients access-denied pattern.
  if (enginePerms.loaded && enginePerms.role !== 'admin' && !enginePerms.can('admin', 'view')) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]" data-testid="billing-items-access-denied">
        <div className="text-center max-w-sm">
          <Lock className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="font-medium text-slate-700">You don't have access to Billing Items</p>
          <p className="text-sm text-slate-500 mt-1">This section requires the <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">admin:view</code> capability. Contact an administrator if you need access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="billing-items-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Billing Items</h1>
          <p className="text-slate-500 mt-1">
            The canonical catalog of billable services & items — job rate lines will select from this list
          </p>
        </div>
        <Button className="btn-navy" onClick={openCreate} data-testid="add-billing-item-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Item
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by description or category..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="billing-item-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={showInactive} onCheckedChange={setShowInactive} id="show-inactive-items" data-testid="billing-items-show-inactive-toggle" />
          <Label htmlFor="show-inactive-items" className="text-sm text-slate-600">Show inactive</Label>
        </div>
        <p className="text-sm text-slate-400 sm:ml-auto" data-testid="billing-items-count">
          {activeCount} active / {items.length} total
        </p>
      </div>

      {loading ? (
        <Card><CardContent className="p-8 text-center text-slate-500">Loading billing items...</CardContent></Card>
      ) : grouped.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center" data-testid="billing-items-empty-state">
            <ReceiptText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">{search ? 'No items match your search' : 'No billing items yet'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {grouped.map(([category, catItems]) => (
            <Card key={category} data-testid={`billing-category-${category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <CardContent className="p-0">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                  <h3 className="font-semibold text-slate-800">{category}</h3>
                  <Badge variant="outline" className="text-xs">{catItems.length}</Badge>
                </div>
                <Table>
                  <TableBody>
                    {catItems.map((item) => (
                      <TableRow key={item.id} data-testid={`billing-item-row-${item.id}`}>
                        <TableCell className={item.is_active ? 'font-medium' : 'font-medium text-slate-400'}>
                          {item.name}
                          {!item.is_active && <Badge variant="secondary" className="ml-2 text-xs">Inactive</Badge>}
                        </TableCell>
                        <TableCell className="text-right w-40">
                          <div className="flex items-center justify-end gap-1">
                            <Switch
                              checked={item.is_active}
                              onCheckedChange={() => toggleActive(item)}
                              className="data-[state=unchecked]:bg-slate-300"
                              data-testid={`billing-item-active-switch-${item.id}`}
                              title={item.is_active ? 'Deactivate' : 'Reactivate'}
                            />
                            <Button variant="ghost" size="sm" onClick={() => openEdit(item)} data-testid={`billing-item-edit-btn-${item.id}`} title="Edit">
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(item)} data-testid={`billing-item-delete-btn-${item.id}`} title="Delete">
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg" data-testid="billing-item-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Billing Item' : 'Add Billing Item'}</DialogTitle>
            <DialogDescription>
              The name is the Invoice Description; the category is a grouping tag only.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name (Invoice Description) *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="billing-item-name-input" />
            </div>
            <div>
              <Label>Category *</Label>
              <Input
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                list="billing-categories"
                placeholder="Pick an existing category or type a new one"
                data-testid="billing-item-category-input"
              />
              <datalist id="billing-categories">
                {categories.map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="billing-item-cancel-btn">Cancel</Button>
              <Button className="btn-navy" onClick={handleSave} disabled={saving} data-testid="save-billing-item-btn">
                {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Create Item'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
